import React, { useState, useMemo, useEffect } from 'react'
import styled, { css } from 'styled-components'
import { buildingRoleLabel, unitRoleLabel } from 'utils/roles'
import { formatDate } from 'utils/date'
import Modal from 'components/atoms/Modal'
import SearchableSelect from 'components/SearchableSelect'
import Avatar from 'components/Avatar'
import { useDispatch } from 'react-redux'
import {
  acceptAccessRequestRoutine,
  denyAccessRequestRoutine
} from 'features/AccessRequest/ducks/actions'
import {
  acceptUnitAccessRequestRoutine,
  denyUnitAccessRequestRoutine
} from 'ducks/units/actions'
import { hideEmail, hidePhoneNumber } from 'utils/user'
import { isNotNilOrEmpty } from 'utils/ramda'
import Select from 'components/atoms/Select'
import Input from 'components/atoms/Input'
import InputDescription from 'features/createBp/components/atoms/InputDescription'
import SearchIcon from '@mui/icons-material/Search'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { isEmpty } from 'ramda'

const BREAKPOINT_MOBILE = 768

const normalizeRelation = (rt) => {
  if (!rt) return null
  if (typeof rt === 'string') return rt
  if (typeof rt === 'object') {
    return rt.value || rt.code || rt.key || rt.type || rt.name || rt.relation || null
  }
  return null
}

const roleText = (raw, isUnit = false) => {
  const key = normalizeRelation(raw)
  if (!key) return ''
  return isUnit ? unitRoleLabel(key) : buildingRoleLabel(key)
}

const useRequestRow = (request, unit, bp, onAccept, onDeny) => {
  const [acceptOpen, setAcceptOpen] = useState(false)
  const [denyOpen, setDenyOpen] = useState(false)
  const [reason, setReason] = useState('no_reason')
  const [other, setOther] = useState('')
  const dispatch = useDispatch()

  const openAccept = () => setAcceptOpen(true)
  const closeAccept = () => setAcceptOpen(false)
  const openDeny = () => setDenyOpen(true)
  const closeDeny = () => setDenyOpen(false)

  const user = request?.user || {}
  const displayName = useMemo(() => {
    const n = [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
    return n || 'Anonymous user'
  }, [user.firstName, user.lastName])

  const roleLabel = roleText(request?.relationType, isNotNilOrEmpty(unit))
  const requestedAt = request?.createdAt ? formatDate(request.createdAt) : null
  const email = user.email || null
  const phone = user.phoneNumber || null

  const doAccept = () => {
    if (onAccept) {
      onAccept(request)
    } else if (isNotNilOrEmpty(unit)) {
      dispatch(acceptUnitAccessRequestRoutine({
        bpId: bp.id,
        unitId: unit.id,
        requestId: request.id
      }))
    } else {
      dispatch(acceptAccessRequestRoutine({
        bpId: request.buildingPassportId,
        requestId: request.id
      }))
    }
    closeAccept()
  }

  const doDeny = () => {
    const r = reason === 'other' ? other : reason
    if (onDeny) {
      onDeny(request, r)
    } else if (isNotNilOrEmpty(unit)) {
      dispatch(denyUnitAccessRequestRoutine({
        bpId: bp.id,
        unitId: unit.id,
        requestId: request.id,
        reason: r
      }))
    } else {
      dispatch(denyAccessRequestRoutine({
        bpId: request.buildingPassportId,
        requestId: request.id,
        reason: r
      }))
    }
    closeDeny()
  }

  return {
    user,
    displayName,
    roleLabel,
    requestedAt,
    email,
    phone,
    acceptOpen,
    denyOpen,
    reason,
    other,
    openAccept,
    closeAccept,
    openDeny,
    closeDeny,
    setReason,
    setOther,
    doAccept,
    doDeny
  }
}

const RequestRowMobile = ({ request, unit, bp, onAccept, onDeny }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const {
    user,
    displayName,
    roleLabel,
    requestedAt,
    email,
    phone,
    acceptOpen,
    denyOpen,
    reason,
    other,
    openAccept,
    closeAccept,
    openDeny,
    closeDeny,
    setReason,
    setOther,
    doAccept,
    doDeny
  } = useRequestRow(request, unit, bp, onAccept, onDeny)

  const toggleExpand = (e) => {
    e?.stopPropagation?.()
    setIsExpanded(!isExpanded)
  }

  return (
    <>
      <MobileCard onClick={toggleExpand}>
        <MobileCardContent>
          <Avatar
            firstName={user.firstName}
            lastName={user.lastName}
            email={user.email}
            src={user.avatar}
            size={30}
          />
          <CardContent>
            <CardTop>
              <NameLabel>{displayName}</NameLabel>
            </CardTop>
            <EmailLabel>{email ? hideEmail(email) : '-'}</EmailLabel>
            {isExpanded && (
              <>
                <CardDetails>
                  {phone && (
                    <DetailRow>
                      <DetailLabel>Phone:</DetailLabel>
                      <DetailValue>{hidePhoneNumber(phone)}</DetailValue>
                    </DetailRow>
                  )}
                  <DetailRow>
                    <DetailLabel>Role:</DetailLabel>
                    <DetailValue>{roleLabel}</DetailValue>
                  </DetailRow>
                  {requestedAt && (
                    <DetailRow>
                      <DetailLabel>Requested:</DetailLabel>
                      <DetailValue>{requestedAt}</DetailValue>
                    </DetailRow>
                  )}
                </CardDetails>
                <ActionButtons onClick={(e) => e.stopPropagation()}>
                  <ActionButton onClick={(e) => { e.stopPropagation(); openAccept(); }}>
                    Accept
                  </ActionButton>
                  <ActionButton $red onClick={(e) => { e.stopPropagation(); openDeny(); }}>
                    Deny
                  </ActionButton>
                </ActionButtons>
              </>
            )}
          </CardContent>
          <ChevronIcon $isExpanded={isExpanded}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </ChevronIcon>
        </MobileCardContent>
      </MobileCard>

      <Modal
        title='Accept request'
        open={acceptOpen}
        onClose={closeAccept}
        onCancel={closeAccept}
        onSubmit={doAccept}
        withCancel
        withSubmit
      >
        Do you really want to accept this request?
      </Modal>

      <Modal
        title='Deny request'
        open={denyOpen}
        onClose={closeDeny}
        onCancel={closeDeny}
        onSubmit={doDeny}
        withCancel
        withSubmit
        disabled={reason === 'other' && !other.trim()}
      >
        <InputDescription margin='0 0 10px 0'>
          Select a reason to send to the requester:
        </InputDescription>

        <Select
          name='reason'
          label='Reason'
          value={reason}
          options={[
            {
              label: 'Do not give reason',
              value: 'no_reason'
            },
            {
              label: 'I do not recognise this person',
              value: 'not_recognised'
            },
            {
              label:
                'I do not wish to share Building Passport information with this person',
              value: 'dont_want_to_share'
            },
            {
              label:
                'The information is sensitive and not able to be shared at present',
              value: 'information_is_sensitive'
            },
            {
              label:
                `The person has applied under the wrong role (e.g. ${unit ? unitRoleLabel('owner') : buildingRoleLabel('owner')} rather than Occupant)`,
              value: 'wrong_role'
            },
            {
              label: 'Other (enter the wording you wish the user to be sent)',
              value: 'other'
            }
          ]}
          onChange={(_, v) => setReason(v)}
        />

        {reason === 'other' && (
          <Input
            name='other'
            label='Your reason'
            multiline
            rows={3}
            value={other}
            onChange={(_, v) => setOther(v)}
          />
        )}
      </Modal>
    </>
  )
}

const RequestRowDesktop = ({ request, unit, bp, onAccept, onDeny }) => {
  const {
    user,
    displayName,
    roleLabel,
    requestedAt,
    email,
    phone,
    acceptOpen,
    denyOpen,
    reason,
    other,
    openAccept,
    closeAccept,
    openDeny,
    closeDeny,
    setReason,
    setOther,
    doAccept,
    doDeny
  } = useRequestRow(request, unit, bp, onAccept, onDeny)

  return (
    <>
      <DesktopTableRow>
        <TableCell>
          <TableRowContent>
            <Avatar
              firstName={user.firstName}
              lastName={user.lastName}
              email={user.email}
              src={user.avatar}
              size={30}
            />
            <NameLabel>{displayName}</NameLabel>
          </TableRowContent>
        </TableCell>
        <TableCell>{email ? hideEmail(email) : '-'}</TableCell>
        <TableCell>{phone ? hidePhoneNumber(phone) : '-'}</TableCell>
        <TableCell>{roleLabel}</TableCell>
        <TableCell>{requestedAt || '-'}</TableCell>
        <TableCell>
          <ActionButtonsInline>
            <ActionButtonSmall onClick={openAccept}>Accept</ActionButtonSmall>
            <ActionButtonSmall $red onClick={openDeny}>Deny</ActionButtonSmall>
          </ActionButtonsInline>
        </TableCell>
      </DesktopTableRow>

      <Modal
        title='Accept request'
        open={acceptOpen}
        onClose={closeAccept}
        onCancel={closeAccept}
        onSubmit={doAccept}
        withCancel
        withSubmit
      >
        Do you really want to accept this request?
      </Modal>

      <Modal
        title='Deny request'
        open={denyOpen}
        onClose={closeDeny}
        onCancel={closeDeny}
        onSubmit={doDeny}
        withCancel
        withSubmit
        disabled={reason === 'other' && !other.trim()}
      >
        <InputDescription margin='0 0 10px 0'>
          Select a reason to send to the requester:
        </InputDescription>

        <Select
          name='reason'
          label='Reason'
          value={reason}
          options={[
            {
              label: 'Do not give reason',
              value: 'no_reason'
            },
            {
              label: 'I do not recognise this person',
              value: 'not_recognised'
            },
            {
              label:
                'I do not wish to share Building Passport information with this person',
              value: 'dont_want_to_share'
            },
            {
              label:
                'The information is sensitive and not able to be shared at present',
              value: 'information_is_sensitive'
            },
            {
              label:
                `The person has applied under the wrong role (e.g. ${unit ? unitRoleLabel('owner') : buildingRoleLabel('owner')} rather than Occupant)`,
              value: 'wrong_role'
            },
            {
              label: 'Other (enter the wording you wish the user to be sent)',
              value: 'other'
            }
          ]}
          onChange={(_, v) => setReason(v)}
        />

        {reason === 'other' && (
          <Input
            name='other'
            label='Your reason'
            multiline
            rows={3}
            value={other}
            onChange={(_, v) => setOther(v)}
          />
        )}
      </Modal>
    </>
  )
}

const RequestSection = ({ requests = [], unit, bp, canAccept, onAccept, onDeny, onCountChange = () => {} }) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(5)

  const filteredRequests = useMemo(() => {
    if (!searchQuery) return requests
    const q = searchQuery.toLowerCase()
    return requests.filter(request => {
      const user = request?.user || {}
      const name = [user.firstName, user.lastName].filter(Boolean).join(' ').toLowerCase()
      const email = (user.email || '').toLowerCase()
      const phone = (user.phoneNumber || '').toLowerCase()
      const role = roleText(request?.relationType, isNotNilOrEmpty(unit)).toLowerCase()
      const created = (request.createdAt || '').toString().toLowerCase()
      return name.includes(q) || email.includes(q) || phone.includes(q) || role.includes(q) || created.includes(q)
    })
  }, [requests, searchQuery])

  useEffect(() => {
    if (typeof onCountChange === 'function') {
      onCountChange(filteredRequests.length)
    }
  }, [filteredRequests, onCountChange])

  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery, itemsPerPage])

  const totalPages = Math.max(1, Math.ceil(filteredRequests.length / itemsPerPage))
  const paginatedRequests = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage
    const end = start + itemsPerPage
    return filteredRequests.slice(start, end)
  }, [filteredRequests, currentPage, itemsPerPage])

  if (!canAccept) return null

  return (
    <Container>
      <SectionHeaderRow>
        <SectionTitle>Access requests ({filteredRequests.length})</SectionTitle>
      </SectionHeaderRow>

      <SearchWrapper>
        <SearchIcon style={{ color: '#94a3b8', fontSize: 20 }} />
        <SearchInput
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </SearchWrapper>

      <MobileListView>
        {!isEmpty(paginatedRequests) ? (
          paginatedRequests.map((request) => (
            <RequestRowMobile
              key={request.id}
              request={request}
              unit={unit}
              bp={bp}
              onAccept={onAccept}
              onDeny={onDeny}
            />
          ))
        ) : (
          <EmptyStateText>No access requests found</EmptyStateText>
        )}
      </MobileListView>

      <DesktopTableView>
        <StyledTable>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Role</th>
              <th>Requested</th>
              <th style={{ width: 150 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!isEmpty(paginatedRequests) ? (
              paginatedRequests.map((request) => (
                <RequestRowDesktop
                  key={request.id}
                  request={request}
                  unit={unit}
                  bp={bp}
                  onAccept={onAccept}
                  onDeny={onDeny}
                />
              ))
            ) : (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: 20, color: '#999' }}>
                  {searchQuery ? 'No access requests match your search' : 'No access requests yet'}
                </td>
              </tr>
            )}
          </tbody>
        </StyledTable>
      </DesktopTableView>

      {filteredRequests.length > 0 && (
        <PaginationWrapper>
          <Pagination>
            <PaginationInfo>
              Showing {(currentPage - 1) * itemsPerPage + 1}
              -{Math.min(currentPage * itemsPerPage, filteredRequests.length)} of {filteredRequests.length} requests
            </PaginationInfo>

            <PaginationControls>
              <DesktopOnlyWrap>
                <PageSizeWrap>
                  <PageSizeLabel>Rows:</PageSizeLabel>
                  <SearchableSelect
                    value={itemsPerPage}
                    onChange={(v) => setItemsPerPage(v)}
                    options={[5, 10, 15, 25, 50].map((size) => ({ value: size, label: String(size) }))}
                    ariaLabel="Rows per page"
                  />
                </PageSizeWrap>
              </DesktopOnlyWrap>

              <IconPaginationButton
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                aria-label="Previous page"
                title="Previous page"
              >
                <ChevronLeftIcon fontSize="small" />
              </IconPaginationButton>

              <DesktopOnlyWrap>
                <PageNumbers>
                  {[...Array(totalPages)].map((_, index) => {
                    const page = index + 1
                    if (
                      totalPages <= 15 ||
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <PageNumber
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          $active={currentPage === page}
                        >
                          {page}
                        </PageNumber>
                      )
                    } else if (page === currentPage - 2 || page === currentPage + 2) {
                      return <Ellipsis key={page}>…</Ellipsis>
                    }
                    return null
                  })}
                </PageNumbers>
              </DesktopOnlyWrap>

              <IconPaginationButton
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                aria-label="Next page"
                title="Next page"
              >
                <ChevronRightIcon fontSize="small" />
              </IconPaginationButton>
            </PaginationControls>
          </Pagination>
        </PaginationWrapper>
      )}
    </Container>
  )
}

export default RequestSection

const Container = styled.div`
  width: 100%;
  padding: 0 16px;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    padding: 0;
  }
`

const SectionHeaderRow = styled.div`
  margin-bottom: 12px;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    padding: 0 16px;
  }
`

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin: 0;
`

const SearchWrapper = styled.div`
  display: flex;
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 10px 14px;
  gap: 10px;
  margin-bottom: 16px;
  transition: all 0.2s;

  &:focus-within {
    background: white;
    border-color: #94a3b8;
    box-shadow: 0 0 0 3px rgba(148, 163, 184, 0.1);
  }

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    margin: 0 16px 20px 16px;
  }
`

const SearchInput = styled.input`
  border: none;
  background: transparent;
  width: 100%;
  font-size: 15px;
  color: #1e293b;
  outline: none;

  &::placeholder {
    color: #94a3b8;
  }
`

const MobileListView = styled.div`
  display: none;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    display: flex;
    flex-direction: column;
  }
`

const MobileCard = styled.div`
  background: white;
  border-bottom: 1px solid #f1f5f9;
  padding: 16px;
  cursor: pointer;
  transition: background 0.1s;

  &:active {
    background: #f8fafc;
  }

  &:last-child {
    border-bottom: none;
  }
`

const MobileCardContent = styled.div`
  display: flex;
  gap: 14px;
  align-items: flex-start;
`

const ChevronIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
  color: #94a3b8;
  transition: transform 0.2s;
  transform: ${({ $isExpanded }) => ($isExpanded ? 'rotate(180deg)' : 'rotate(0deg)')};
  flex-shrink: 0;
`

const CardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`

const CardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

const NameLabel = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
`

const EmailLabel = styled.span`
  font-size: 13px;
  color: #94a3b8;
`

const CardDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
`

const DetailRow = styled.div`
  display: flex;
  gap: 8px;
  font-size: 13px;
`

const DetailLabel = styled.span`
  color: #64748b;
  font-weight: 500;
`

const DetailValue = styled.span`
  color: #1e293b;
`

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
  flex-wrap: wrap;
`

const ActionButton = styled.button`
  padding: 8px 16px;
  border: 1px solid ${({ $red }) => ($red ? '#d14724' : '#10b981')};
  background: ${({ $red }) => ($red ? '#d14724' : '#10b981')};
  color: white;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
  }

  &:active {
    transform: scale(0.98);
  }
`

const DesktopTableView = styled.div`
  width: 100%;
  overflow-x: auto;
  background: white;
  border-radius: 8px;
  border: 1px solid #f1f5f9;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    display: none;
  }
`

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  thead {
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
  }

  th {
    text-align: left;
    padding: 12px 16px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    color: #64748b;
    letter-spacing: 0.5px;
  }

  tbody tr {
    border-bottom: 1px solid #f1f5f9;
    transition: background 0.1s;

    &:hover {
      background: #f8fafc;
    }

    &:last-child {
      border-bottom: none;
    }
  }
`

const DesktopTableRow = styled.tr``

const TableCell = styled.td`
  padding: 14px 16px;
  font-size: 14px;
  color: #1e293b;
`

const TableRowContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const ActionButtonsInline = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`

const ActionButtonSmall = styled.button`
  padding: 6px 12px;
  border: 1px solid ${({ $red }) => ($red ? '#d14724' : '#10b981')};
  background: ${({ $red }) => ($red ? '#d14724' : '#10b981')};
  color: white;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
  }
`

const EmptyStateText = styled.div`
  padding: 30px;
  text-align: center;
  color: #94a3b8;
  font-size: 14px;
`

const PaginationWrapper = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #f1f5f9;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    padding: 0 16px;
    margin-top: 16px;
    padding-top: 16px;
  }
`

const Pagination = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  flex-wrap: wrap;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`

const PaginationInfo = styled.div`
  color: #666;
  font-size: 14px;
  flex: 1;
  text-align: center;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    font-size: 12px;
    order: -1;
    width: 100%;
  }
`

const DesktopOnlyWrap = styled.div`
  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    display: none;
  }
`

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
  flex-wrap: wrap;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    margin-left: 0;
    width: 100%;
    justify-content: space-between;
    gap: 12px;
  }
`

const PageSizeWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 640px) {
    width: 100%;
    justify-content: center;
    padding-top: 8px;
    border-top: 1px solid #e0e0e0;
  }
`

const PageSizeLabel = styled.span`
  color: #666;
  font-size: 13px;

  @media (max-width: 640px) {
    font-size: 12px;
  }
`

const PageNumbers = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;

  @media (max-width: 640px) {
    gap: 4px;
  }
`

const PageNumber = styled.button`
  width: 32px;
  height: 32px;
  border: 1px solid ${({ $active }) => ($active ? '#d14724' : '#e0e0e0')};
  background: ${({ $active }) => ($active ? '#d14724' : 'white')};
  color: ${({ $active }) => ($active ? 'white' : '#333')};
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover {
    background: ${({ $active }) => ($active ? '#d14724' : '#f5f5f5')};
    border-color: #d14724;
    color: ${({ $active }) => ($active ? 'white' : '#d14724')};
  }

  @media (max-width: 640px) {
    width: 28px;
    height: 28px;
    font-size: 12px;
  }
`

const Ellipsis = styled.span`
  color: #999;
  padding: 0 5px;

  @media (max-width: 640px) {
    padding: 0 2px;
  }
`

const IconPaginationButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 32px;
  border: 1px solid #e0e0e0;
  background: white;
  color: #333;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: #f5f5f5;
    border-color: #d14724;
    color: #d14724;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 640px) {
    width: 32px;
    height: 28px;
  }
`
